import React, { useRef, useEffect, useState, KeyboardEvent } from 'react';
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { AutoAwesome, DeleteOutline, Send, Stop } from '@mui/icons-material';
import useAbeStore from '../../store/zustand/abe';
// eslint-disable-next-line import/no-named-as-default
import useAbe from '../../hooks/useAbe';
import AbeMessageBubble from './AbeMessage';
import { mainColors } from '../../theme/themeConfig';

const QUICK_REPLIES = [
  'Do I have any overdue invoices?',
  'Which technician worked the most jobs this week?',
  'Show me upcoming jobs for this week',
  'Who are my most active clients?'
];

interface EmptyStateProps {
  onQuickReply: (text: string) => void;
}

function AbeChatEmptyState({ onQuickReply }: EmptyStateProps): JSX.Element {
  return (
    <Box
      sx={{
        alignItems: 'center',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        justifyContent: 'center',
        pb: 2,
        px: 2
      }}
    >
      <Box
        sx={{
          alignItems: 'center',
          bgcolor: mainColors.MAIN,
          borderRadius: '50%',
          display: 'flex',
          height: 52,
          justifyContent: 'center',
          mb: 1.5,
          width: 52
        }}
      >
        <AutoAwesome sx={{ color: '#fff', fontSize: 26 }} />
      </Box>
      <Typography
        sx={{ color: 'text.primary', fontWeight: 600, mb: 0.5 }}
        variant="body2"
      >
        Ask Abe anything about your business
      </Typography>
      <Typography
        sx={{
          color: 'text.secondary',
          lineHeight: 1.6,
          mb: 2.5,
          textAlign: 'center'
        }}
        variant="caption"
      >
        I have access to your live data — clients, projects, invoices, margins,
        and team.
      </Typography>
      <Box
        sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}
      >
        {QUICK_REPLIES.map((text) => (
          <Button
            key={text}
            onClick={() => onQuickReply(text)}
            size="small"
            sx={{
              '&:hover': {
                borderColor: mainColors.MAIN,
                color: mainColors.MAIN
              },
              borderColor: 'divider',
              color: 'text.secondary',
              fontSize: '0.78rem',
              justifyContent: 'flex-start',
              py: 0.75,
              textTransform: 'none'
            }}
            variant="outlined"
          >
            {text}
          </Button>
        ))}
      </Box>
    </Box>
  );
}

function AbeChat(): JSX.Element {
  const messages = useAbeStore((s) => s.messages);
  const isStreaming = useAbeStore((s) => s.isStreaming);
  const thinkingStatus = useAbeStore((s) => s.thinkingStatus);
  const prefillMessage = useAbeStore((s) => s.prefillMessage);
  const setPrefillMessage = useAbeStore((s) => s.setPrefillMessage);
  const { confirmAction, resetSession, sendMessage, stopStreaming } = useAbe();

  const [input, setInput] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Populate input from prefill (triggered by "Ask Abe to update" in dashboard)
  useEffect(() => {
    if (prefillMessage) {
      setInput(prefillMessage);
      setPrefillMessage(null);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [prefillMessage, setPrefillMessage]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = () => {
    const text = input.trim();
    if (!text || isStreaming) return;
    setInput('');
    sendMessage(text);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleDismissConfirmation = (msgId: string) => {
    useAbeStore.getState().updateMessage(msgId, {
      confirmationPayload: undefined,
      requiresConfirmation: false
    });
    useAbeStore.getState().setPendingConfirmation(null);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden'
      }}
    >
      <Box sx={{ flex: 1, overflowY: 'auto', px: 1.5, py: 1.5 }}>
        {messages.length === 0 ? (
          <AbeChatEmptyState
            onQuickReply={(text) => {
              setInput(text);
              inputRef.current?.focus();
            }}
          />
        ) : (
          <>
            {messages.map((msg) => (
              <AbeMessageBubble
                key={msg.id}
                message={msg}
                onConfirm={(payload) => confirmAction(payload)}
                onDismiss={() => handleDismissConfirmation(msg.id)}
                thinkingStatus={
                  msg.isStreaming && !msg.content ? thinkingStatus : undefined
                }
              />
            ))}
            <div ref={messagesEndRef} />
          </>
        )}
      </Box>

      <Box
        sx={{
          bgcolor: 'background.paper',
          borderColor: 'divider',
          borderTop: '1px solid',
          pb: 1.5,
          pt: 1,
          px: 1.5
        }}
      >
        {messages.length > 0 && !isStreaming && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5 }}>
            <Tooltip title="Clear conversation">
              <IconButton
                onClick={resetSession}
                size="small"
                sx={{ color: 'text.disabled' }}
              >
                <DeleteOutline fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        )}

        <TextField
          fullWidth
          multiline
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {isStreaming ? (
                  <Tooltip title="Stop">
                    <IconButton
                      onClick={stopStreaming}
                      size="small"
                      sx={{ color: 'error.main' }}
                    >
                      <Stop fontSize="small" />
                    </IconButton>
                  </Tooltip>
                ) : (
                  <IconButton
                    disabled={!input.trim()}
                    onClick={handleSend}
                    size="small"
                    sx={{
                      color: input.trim() ? mainColors.MAIN : 'text.disabled',
                      transition: 'color 0.15s'
                    }}
                  >
                    <Send fontSize="small" />
                  </IconButton>
                )}
              </InputAdornment>
            )
          }}
          disabled={isStreaming}
          inputRef={inputRef}
          maxRows={4}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Ask Abe anything…"
          size="small"
          sx={{
            '& .MuiOutlinedInput-root': {
              '&.Mui-focused fieldset': { borderColor: mainColors.MAIN },
              borderRadius: 3,
              fontSize: '0.875rem'
            }
          }}
          value={input}
        />
      </Box>
    </Box>
  );
}

export default AbeChat;
